import React, { useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    Show,
    SimpleShowLayout,
    ReferenceField,
    usePermissions,
    TopToolbar,
    DeleteButton,
    Button,
    useRecordContext,
    useNotify,
    useRefresh,
    ChipField,
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    required,
    email as validateEmail,
    CreateButton,
    Pagination,
    ShowButton,
} from 'react-admin';
import {
    Box,
    Chip,
    LinearProgress,
    Card,
    CardContent,
    Typography,
    Divider
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import QueueIcon from '@mui/icons-material/Queue';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { API_URL } from '../config';
import { JumpstartTextInput, JumpstartSelectInput, JumpstartFormToolbar } from '../components/JumpstartInputs';
import { useT } from '../i18n/useT';

const getDownloadFilename = (contentDisposition: string | null, fallback: string): string => {
    if (!contentDisposition) {
        return fallback;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch {
            // Fall through to basic filename parsing.
        }
    }

    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return filenameMatch?.[1] || fallback;
};

// Status chip with color coding
const StatusChip = ({ status }: { status: number }) => {
    const t = useT();
    const statusMap: { [key: number]: { labelKey: string; color: 'default' | 'primary' | 'success' | 'error'; icon: React.ReactElement } } = {
        0: { labelKey: 'resources.queue.statuses.pending',    color: 'default', icon: <PendingIcon /> },
        1: { labelKey: 'resources.queue.statuses.processing', color: 'primary', icon: <HourglassEmptyIcon /> },
        2: { labelKey: 'resources.queue.statuses.completed',  color: 'success', icon: <CheckCircleIcon /> },
        3: { labelKey: 'resources.queue.statuses.error',      color: 'error',   icon: <ErrorIcon /> },
    };

    const statusInfo = statusMap[status] || { labelKey: 'resources.queue.statuses.unknown', color: 'default' as const, icon: <PendingIcon /> };

    return (
        <Chip
            label={t(statusInfo.labelKey as any)}
            color={statusInfo.color}
            size="small"
            icon={statusInfo.icon}
        />
    );
};

// Download button for completed queue items
const DownloadQueueButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const t = useT();

    if (!record || record.status !== 2) return null;

    const handleDownload = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                notify(t('notifications.authRequired'), { type: 'error' });
                return;
            }

            const response = await fetch(`${API_URL}/queue/${record.queue_id}/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Download failed');
            }

            // Get the filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            const filename = getDownloadFilename(contentDisposition, `document_${record.queue_id}.pdf`);

            // Create blob and trigger download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            notify(t('notifications.downloadSuccess'), { type: 'success' });
        } catch (error) {
            console.error('Download error:', error);
            notify(t('notifications.downloadFailed'), { type: 'error' });
        }
    };

    return (
        <Button
            label="actions.download"
            onClick={handleDownload}
            startIcon={<CloudDownloadIcon />}
        />
    );
};

// List actions toolbar
const QueueListActions = () => {
    return (
        <TopToolbar>
            <RefreshStatusButton />
            <CreateButton />
        </TopToolbar>
    );
};

// Refresh status button
const RefreshStatusButton = () => {
    const refresh = useRefresh();
    const notify = useNotify();
    const t = useT();

    const handleRefresh = () => {
        refresh();
        notify(t('resources.queue.notifications.statusRefreshed'), { type: 'info' });
    };

    return (
        <Button
            label="actions.refresh"
            onClick={handleRefresh}
            startIcon={<RefreshIcon />}
        />
    );
};

// List of queue items
export const QueueList = () => {
    const { permissions } = usePermissions();
    const t = useT();

    return (
        <List
            sort={{ field: 'created_at', order: 'DESC' }}
            perPage={25}
            pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
            empty={<div style={{ padding: '1rem', textAlign: 'center' }}>
                <h3>{t('resources.queue.empty.title')}</h3>
                <p>{t('resources.queue.empty.hint')}</p>
            </div>}
            actions={<QueueListActions />}
        >
            <Datagrid rowClick={false}>
                <TextField source="queue_id" />
                <TextField source="sku" />
                <TextField source="processing_type" />
                <FunctionField
                    source="status"
                    render={(record: any) => <StatusChip status={record.status} />}
                />
                <FunctionField
                    source="progress"
                    render={(record: any) => (
                        <Box display="flex" alignItems="center" style={{ minWidth: 100 }}>
                            <Box width="100%" mr={1}>
                                <LinearProgress
                                    variant="determinate"
                                    value={record.progress || 0}
                                    color={record.status === 3 ? 'error' : 'primary'}
                                />
                            </Box>
                            <Box minWidth={35}>
                                <Typography variant="body2" color="textSecondary">
                                    {`${record.progress || 0}%`}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                />
                <DateField source="created_at" showTime />
                <ShowButton />
                <DownloadQueueButton />
                {permissions === 'admin' && <DeleteButton />}
            </Datagrid>
        </List>
    );
};

// Show queue item details
const QueueShowActions = () => {
    const { permissions } = usePermissions();
    return (
        <TopToolbar>
            <RefreshStatusButton />
            <DownloadQueueButton />
            {permissions === 'admin' && <DeleteButton />}
        </TopToolbar>
    );
};

export const QueueShow = () => {
    const t = useT();
    return (
        <Show actions={<QueueShowActions />}>
            <SimpleShowLayout>
                <TextField source="queue_id" />
                <TextField source="sku" />
                <TextField source="processing_type" />
                <FunctionField
                    source="status"
                    render={(record: any) => <StatusChip status={record.status} />}
                />
                <FunctionField
                    source="progress"
                    render={(record: any) => (
                        <Box display="flex" alignItems="center" style={{ width: 300 }}>
                            <Box width="100%" mr={1}>
                                <LinearProgress
                                    variant="determinate"
                                    value={record.progress || 0}
                                    color={record.status === 3 ? 'error' : 'primary'}
                                />
                            </Box>
                            <Box minWidth={35}>
                                <Typography variant="body2" color="textSecondary">
                                    {`${record.progress || 0}%`}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                />
                
                <Divider style={{ marginTop: '1rem', marginBottom: '1rem' }} />
                <Typography variant="h6" gutterBottom>{t('resources.queue.sections.consumer')}</Typography>
                <TextField source="consumer_name" />
                <TextField source="consumer_surname" />
                <TextField source="consumer_address" />
                <TextField source="consumer_telephone" />
                <TextField source="consumer_email" />
                
                <Divider style={{ marginTop: '1rem', marginBottom: '1rem' }} />
                
                <TextField source="error_code" />
                <TextField source="error_message" />
                <TextField source="backchannel_url" />
                
                <DateField source="created_at" showTime />
                <DateField source="updated_at" showTime />
            </SimpleShowLayout>
        </Show>
    );
};

// Create async queue job
export const QueueCreate = () => {
    const notify = useNotify();
    const refresh = useRefresh();
    const t = useT();
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (data: any) => {
        setSubmitting(true);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                notify(t('notifications.authRequired'), { type: 'error' });
                return;
            }

            const requestBody = {
                processing_type: data.processing_type,
                sku: data.sku,
                consumer: {
                    name: data.name,
                    surname: data.surname,
                    address: data.address,
                    telephone_number: data.telephone_number,
                    email: data.email
                },
                backchannel_url: data.backchannel_url || undefined
            };

            const response = await fetch(`${API_URL}/queue/sign`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to sign');
            }

            const result = await response.json();
            notify(t('resources.queue.notifications.queuedSuccess', { queueId: result.queue_id }), { type: 'success' });
            refresh();
            
            // Redirect to the queue list
            window.location.href = '/#/queue';
        } catch (error: any) {
            notify(`${t('resources.queue.notifications.signFailedPrefix')}: ${error.message}`, { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Create title={t('resources.queue.create.title')}>
            <SimpleForm onSubmit={handleSubmit} toolbar={<JumpstartFormToolbar saveLabel={t('resources.queue.create.saveLabel')} />}>
                <Typography variant="h6" gutterBottom style={{ width: '100%' }}>
                    {t('resources.queue.sections.document')}
                </Typography>
                
                <JumpstartSelectInput
                    source="processing_type"
                    label={t('resources.queue.form.processingType')}
                    choices={[
                        { id: 'watermark', name: t('resources.queue.processingTypes.watermark') },
                        { id: 'sign', name: t('resources.queue.processingTypes.sign') },
                    ]}
                    validate={[required()]}
                    defaultValue="watermark"
                />
                
                <JumpstartTextInput source="sku" label={t('resources.queue.form.publicationSku')} validate={[required()]} fullWidth />
                
                <Divider style={{ margin: '1rem 0', width: '100%' }} />
                <Typography variant="h6" gutterBottom style={{ width: '100%' }}>
                    {t('resources.queue.sections.consumer')}
                </Typography>
                
                <JumpstartTextInput source="name" label={t('resources.queue.form.firstName')} validate={[required()]} fullWidth />
                <JumpstartTextInput source="surname" label={t('resources.queue.form.lastName')} validate={[required()]} fullWidth />
                <JumpstartTextInput source="address" label={t('resources.queue.form.address')} validate={[required()]} fullWidth multiline />
                <JumpstartTextInput source="telephone_number" label={t('resources.queue.form.telephone')} validate={[required()]} fullWidth />
                <JumpstartTextInput source="email" label={t('resources.queue.form.email')} validate={[required(), validateEmail()]} fullWidth />
                
                <Divider style={{ margin: '1rem 0', width: '100%' }} />
                <Typography variant="h6" gutterBottom style={{ width: '100%' }}>
                    {t('resources.queue.sections.optional')}
                </Typography>
                
                <JumpstartTextInput
                    source="backchannel_url"
                    label={t('resources.queue.form.backchannelUrl')}
                    helperText={t('resources.queue.helpers.backchannelUrl')}
                    fullWidth
                />
                
                <Box mt={2}>
                    <Typography variant="body2" color="textSecondary">
                        {t('resources.queue.summaryText')}
                    </Typography>
                </Box>
            </SimpleForm>
        </Create>
    );
};

export const queueIcon = QueueIcon;
